import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Cloud,
  Users,
  Calendar,
  FileText,
  Image,
  HardDrive,
  CheckCircle,
  XCircle,
  RefreshCw,
  LogIn,
  LogOut,
  Loader2
} from 'lucide-react';
import { icloud } from '../lib/icloud/client';

interface ICloudSyncProps {
  onClose?: () => void;
}

export function ICloudSync({ onClose }: ICloudSyncProps) {
  const [authenticated, setAuthenticated] = useState(false);
  const [appleId, setAppleId] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [syncing, setSyncing] = useState<string | null>(null);
  const [syncStatus, setSyncStatus] = useState({
    contacts: false,
    calendar: false,
    notes: false,
    photos: false,
    drive: false
  });

  useEffect(() => {
    setAuthenticated(icloud.isAuthenticated());
    setSyncStatus(icloud.getSyncStatus());
  }, []);

  const handleLogin = async () => {
    setLoading(true);
    try {
      const success = await icloud.login(appleId, password);
      if (success) {
        setAuthenticated(true);
        setPassword('');
      }
    } catch (error) {
      console.error('Login failed:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    icloud.logout();
    setAuthenticated(false);
    setAppleId('');
    setSyncStatus({
      contacts: false,
      calendar: false,
      notes: false,
      photos: false,
      drive: false
    });
  };

  const handleSync = async (service: string) => {
    setSyncing(service);
    try {
      switch (service) {
        case 'contacts':
          await icloud.syncContacts();
          break;
        case 'calendar':
          await icloud.syncCalendar();
          break;
        // Add other services
      }
      setSyncStatus(icloud.getSyncStatus());
    } catch (error) {
      console.error(`Sync ${service} failed:`, error);
    } finally {
      setSyncing(null);
    }
  };

  const services = [
    { id: 'contacts', name: 'Contacts', icon: Users, color: '#8E8E93' },
    { id: 'calendar', name: 'Calendar', icon: Calendar, color: '#FF3B30' },
    { id: 'notes', name: 'Notes', icon: FileText, color: '#FFCC00' },
    { id: 'photos', name: 'Photos', icon: Image, color: '#FF9500' },
    { id: 'drive', name: 'iCloud Drive', icon: HardDrive, color: '#007AFF' }
  ];

  return (
    <div className="min-h-screen bg-macos-gray-100 dark:bg-macos-gray-950 p-6">
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <div className="window-macos glass-macos bg-white/80 dark:bg-macos-gray-900/80 p-6 mb-6">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-macos bg-gradient-to-br from-macos-blue to-macos-teal flex items-center justify-center shadow-macos">
                <Cloud className="w-6 h-6 text-white" />
              </div>
              <div>
                <h1 className="text-xl font-semibold text-macos-gray-950 dark:text-white">
                  iCloud Sync
                </h1>
                <p className="text-sm text-macos-gray-600 dark:text-macos-gray-400">
                  {authenticated ? `Signed in as ${appleId}` : 'Connect your iCloud account'}
                </p>
              </div>
            </div>
            {onClose && (
              <button
                onClick={onClose}
                className="w-8 h-8 rounded-full hover:bg-macos-gray-200 dark:hover:bg-macos-gray-800 flex items-center justify-center transition-colors"
              >
                ×
              </button>
            )}
          </div>
        </div>

        <AnimatePresence mode="wait">
          {!authenticated ? (
            <motion.div
              key="login"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              className="window-macos glass-macos bg-white/80 dark:bg-macos-gray-900/80 p-6"
            >
              <h2 className="text-lg font-semibold text-macos-gray-950 dark:text-white mb-4">
                Sign In
              </h2>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-macos-gray-700 dark:text-macos-gray-300 mb-2">
                    Apple ID
                  </label>
                  <input
                    type="email"
                    value={appleId}
                    onChange={(e) => setAppleId(e.target.value)}
                    placeholder="your@email.com"
                    className="w-full px-4 py-2 rounded-macos bg-macos-gray-100 dark:bg-macos-gray-800 border border-macos-gray-300 dark:border-macos-gray-700 text-macos-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-macos-blue"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-macos-gray-700 dark:text-macos-gray-300 mb-2">
                    Password
                  </label>
                  <input
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="••••••••"
                    onKeyPress={(e) => e.key === 'Enter' && handleLogin()}
                    className="w-full px-4 py-2 rounded-macos bg-macos-gray-100 dark:bg-macos-gray-800 border border-macos-gray-300 dark:border-macos-gray-700 text-macos-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-macos-blue"
                  />
                </div>

                <button
                  onClick={handleLogin}
                  disabled={loading || !appleId || !password}
                  className="w-full btn-macos flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loading ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Signing In...
                    </>
                  ) : (
                    <>
                      <LogIn className="w-4 h-4" />
                      Sign In
                    </>
                  )}
                </button>

                <p className="text-xs text-macos-gray-500 dark:text-macos-gray-500 text-center mt-4">
                  Note: This is a demo integration. Use an app-specific password for production.
                </p>
              </div>
            </motion.div>
          ) : (
            <motion.div
              key="services"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              className="space-y-4"
            >
              {/* Services */}
              <div className="window-macos glass-macos bg-white/80 dark:bg-macos-gray-900/80 p-6">
                <h2 className="text-lg font-semibold text-macos-gray-950 dark:text-white mb-4">
                  Sync Services
                </h2>
                
                <div className="space-y-3">
                  {services.map((service) => {
                    const Icon = service.icon;
                    const isSynced = syncStatus[service.id as keyof typeof syncStatus];
                    const isSyncing = syncing === service.id;
                    
                    return (
                      <div
                        key={service.id}
                        className="flex items-center justify-between p-4 rounded-macos bg-macos-gray-50 dark:bg-macos-gray-800 hover:bg-macos-gray-100 dark:hover:bg-macos-gray-700 transition-colors"
                      >
                        <div className="flex items-center gap-3">
                          <div
                            className="w-10 h-10 rounded-macos flex items-center justify-center shadow-sm"
                            style={{ backgroundColor: service.color }}
                          >
                            <Icon className="w-5 h-5 text-white" />
                          </div>
                          <div>
                            <h3 className="font-medium text-macos-gray-900 dark:text-white">
                              {service.name}
                            </h3>
                            <p className="text-xs text-macos-gray-600 dark:text-macos-gray-400">
                              {isSynced ? 'Synced' : 'Not synced'}
                            </p>
                          </div>
                        </div>
                        
                        <div className="flex items-center gap-2">
                          {isSynced && !isSyncing && (
                            <CheckCircle className="w-5 h-5 text-macos-green" />
                          )}
                          
                          <button
                            onClick={() => handleSync(service.id)}
                            disabled={isSyncing}
                            className="p-2 rounded-full hover:bg-macos-gray-200 dark:hover:bg-macos-gray-600 transition-colors disabled:opacity-50"
                          >
                            {isSyncing ? (
                              <Loader2 className="w-4 h-4 animate-spin text-macos-blue" />
                            ) : (
                              <RefreshCw className="w-4 h-4 text-macos-gray-600 dark:text-macos-gray-400" />
                            )}
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Logout */}
              <button
                onClick={handleLogout}
                className="w-full px-4 py-2 rounded-macos bg-macos-gray-200 dark:bg-macos-gray-800 text-macos-gray-900 dark:text-white hover:bg-macos-gray-300 dark:hover:bg-macos-gray-700 transition-colors flex items-center justify-center gap-2"
              >
                <LogOut className="w-4 h-4" />
                Sign Out
              </button>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}
